import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, ShoppingBag, User, LogOut, LayoutDashboard, Headphones } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/lib/hooks";

export const Logo = ({ light = false }) => (
  <span className={`font-display text-2xl sm:text-[28px] font-semibold tracking-tight whitespace-nowrap ${light ? "text-white" : "text-[#172554]"}`}>
    online<span className="font-extrabold text-[#16A34A]">satiyorum</span>
    <span className={light ? "text-white/70" : "text-slate-400"}>.com</span>
  </span>
);

function SearchBox({ className = "" }) {
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const nav = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    nav(q ? `/?q=${encodeURIComponent(q)}` : "/");
  };
  return (
    <form onSubmit={submit} className={`relative ${className}`}>
      <input
        data-testid="header-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Ürün, marka veya çeşit ara (ör. tavuklu)"
        className="w-full h-12 rounded-md border border-slate-300 bg-white pl-4 pr-12 text-sm outline-none focus:border-[#172554] focus:ring-2 focus:ring-[#172554]/15"
      />
      <button data-testid="header-search-button" className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[#172554]" aria-label="Ara">
        <Search className="h-5 w-5" />
      </button>
    </form>
  );
}

function Account() {
  const { user, signOut } = useAuth();
  if (!user)
    return (
      <div className="flex items-center gap-4 text-sm font-semibold text-slate-800">
        <Link data-testid="header-login-link" to="/giris" className="hover:text-[#172554] flex items-center gap-1.5">
          <User className="h-5 w-5 sm:hidden" /><span className="hidden sm:inline">Giriş Yap</span>
        </Link>
        <Link data-testid="header-register-link" to="/giris?tab=kayit" className="hidden sm:inline hover:text-[#172554]">Kayıt Ol</Link>
      </div>
    );
  return (
    <div className="flex items-center gap-3 text-sm font-semibold">
      {user.role === "admin" ? (
        <Link data-testid="header-admin-link" to="/yonetim" className="flex items-center gap-1.5 text-[#172554]">
          <LayoutDashboard className="h-5 w-5" /><span className="hidden sm:inline">Yönetim</span>
        </Link>
      ) : (
        <Link data-testid="header-account-link" to="/hesabim" className="flex items-center gap-1.5 text-slate-800 hover:text-[#172554]">
          <User className="h-5 w-5" /><span className="hidden sm:inline max-w-[110px] truncate">{user.name}</span>
        </Link>
      )}
      <button data-testid="header-logout-button" onClick={signOut} className="text-slate-500 hover:text-red-600" aria-label="Çıkış">
        <LogOut className="h-5 w-5" />
      </button>
    </div>
  );
}

export default function Header() {
  const { count, setOpen } = useCart();
  const s = useSettings();
  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 lg:px-6 h-[72px] lg:h-[88px] flex items-center gap-6">
        <Link to="/" data-testid="header-logo-link"><Logo /></Link>
        <SearchBox className="hidden md:block flex-1 max-w-xl mx-auto" />
        <div className="ml-auto flex items-center gap-5">
          {(s?.support_phone || s?.whatsapp_number) && (
            <div className="hidden xl:flex items-center gap-2 text-[#172554]" data-testid="header-support">
              <Headphones className="h-6 w-6" />
              <div className="leading-tight">
                <div className="text-[11px] font-bold uppercase tracking-wider">Sipariş & Destek</div>
                <div className="text-sm font-semibold">{s.support_phone || s.whatsapp_number}</div>
              </div>
            </div>
          )}
          <Account />
          <button data-testid="header-cart-button" onClick={() => setOpen(true)} className="relative p-1 text-slate-900 hover:text-[#172554]" aria-label="Sepet">
            <ShoppingBag className="h-7 w-7" />
            {count > 0 && (
              <span data-testid="header-cart-count" className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-[#16A34A] text-white text-[11px] font-bold flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>
      <div className="md:hidden px-4 pb-3"><SearchBox /></div>
    </header>
  );
}
